import { useState, useMemo, useCallback, useEffect } from 'react';
import { MapPin, TrendingUp, Share2, Heart, Download, ChevronRight, Percent, Shield, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Tabs, TabsContent } from '../ui/tabs';

import { DetailPageHeader } from '../ui/DetailPageHeader';
import { DetailTabList } from '../ui/DetailTabList';
import { StatBox, StatGrid } from '../ui/StatBox';
import { MobileCTA } from '../ui/MobileCTA';
import { RiskDisclosure } from '../ui/RiskDisclosure';
import PropertyMap from '../../PropertyMap';

import { PropertyDetailProps, PropertyDocument } from './PropertyDetail.types';
import { getPropertyTypeDisplay, formatCurrency } from './PropertyDetail.utils';
import { PropertyGallery } from './PropertyGallery';
import api from '../../../services/api';
import { PageRenderer } from '../PageBuilder';
import type { PageConfig } from '../PageBuilder/types';

export function PropertyDetail({
  property,
  onClose,
  onInvest,
  performanceData,
  distributionData,
  financialMetrics,
  investmentHighlights,
  documents,
  marketStatistics,
  nearbyAmenities,
  isSaved: isSavedProp = false,
  onSaveToggle,
  onShare,
  onDownloadProspectus,
  onDocumentDownload,
}: PropertyDetailProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Lock body scroll while the overlay is open so the page behind cannot scroll
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Use internal state only if no external control is provided
  const [isSavedInternal, setIsSavedInternal] = useState(false);
  const isSaved = onSaveToggle ? isSavedProp : isSavedInternal;
  const handleSaveToggle = useCallback(() => {
    if (onSaveToggle) {
      onSaveToggle(!isSaved);
    } else {
      setIsSavedInternal((prev) => !prev);
    }
  }, [onSaveToggle, isSaved]);

  // Fetch documents from API when component mounts
  const [fetchedDocs, setFetchedDocs] = useState<PropertyDocument[]>([]);
  useEffect(() => {
    if (documents && documents.length > 0) return; // Use prop if provided
    const loadDocs = async () => {
      try {
        const res: any = await api.get(`/properties/${property.id}/documents`);
        const body = (res?.data as any)?.success !== undefined ? res.data : res?.data || res;
        const apiDocs = (body?.data || []).map((d: any) => ({
          id: d.id,
          name: d.title || d.filename,
          size: d.file_size_bytes ? formatBytes(d.file_size_bytes) : '',
          url: d.download_url,
        }));
        setFetchedDocs(apiDocs);
      } catch { /* ignore */ }
    };
    loadDocs();
  }, [property.id, documents]);

  const effectiveDocs = (documents && documents.length > 0) ? documents : fetchedDocs;

  // Load Page Builder config for this property (if linked)
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  useEffect(() => {
    const loadPageConfig = async () => {
      try {
        const configRes: any = await api.get(`/properties/${property.id}/page-config`);
        // Backend wraps as { success, data: { blocks, settings } }
        const body = configRes?.data;
        const cfg = body?.data || body;
        if (cfg?.blocks?.length) {
          // Strip hero blocks — the property detail already has its own gallery + header.
          // Hero blocks in the page builder are for full-page layouts, not embedded tabs.
          const tabBlocks = cfg.blocks.filter((b: any) => b.blockKey !== 'hero');
          if (tabBlocks.length) {
            setPageConfig({ ...cfg, blocks: tabBlocks });
          }
        }
      } catch { /* no page builder content */ }
    };
    loadPageConfig();
  }, [property.id]);

  // Derived values - no state needed, these are pure computations
  const sharePrice = property.price / (property.totalShares || 1);
  const availableShares = property.availableShares ?? 0;
  const propertyValue = property.price;
  // Derive funded status from available shares, not hardcoded status field
  const isFunded = availableShares === 0;
  const effectiveFundedPct = isFunded ? 100 : property.fundedPercentage;

  const formatBytes = (b: number) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const [tabContent] = useState<Record<string, string>>(property.tabContent || {});

  // Property images - memoized to maintain stable reference for gallery
  const propertyImages = useMemo(() => {
    if (property.images && property.images.length > 0) {
      return property.images;
    }
    return [property.imageUrl];
  }, [property.images, property.imageUrl]);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
  }, [propertyImages.length]);

  const previousImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
  }, [propertyImages.length]);

  return (
    <div className="fixed inset-0 bg-background z-50 overflow-y-auto portal-typography">
      <DetailPageHeader onClose={onClose} />

      {/* Main Content */}
      <div className="container mx-auto px-0 md:px-4 lg:px-6 pb-24 md:pb-6 max-w-7xl">
        {/* Image Gallery */}
        <PropertyGallery
          images={propertyImages}
          propertyName={property.name}
          fundedPercentage={property.fundedPercentage}
          currentIndex={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
          onPrevious={previousImage}
          onNext={nextImage}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-6 mt-0 lg:mt-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 px-4 md:px-0">
            {/* Property Header */}
            <div className={`py-4 md:py-6 ${!pageConfig ? 'border-b border-border' : ''}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-sans font-semibold text-[#242424] mb-2">{property.name}</h1>
                  <div className="flex items-center gap-2 text-muted-foreground mb-3">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm md:text-base">{property.city}, {property.state}</span>
                  </div>
                </div>
                {financialMetrics?.appreciationYTD && (
                  <div className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/20">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-primary">{financialMetrics.appreciationYTD}</span>
                  </div>
                )}
              </div>

              {/* Key Stats — only shown when no page builder config; stats are controlled via page builder stats-grid block when configured */}
              {!pageConfig && (isFunded ? (
                <StatGrid columns={2}>
                  <StatBox label="Property Value" value={formatCurrency(propertyValue)} />
                  <StatBox label="Amount Raised" value={formatCurrency(propertyValue)} />
                </StatGrid>
              ) : (
                <StatGrid columns={4}>
                  <StatBox label="Share Price" value={formatCurrency(sharePrice)} />
                  <StatBox label="Annual Return" value={<span className="text-[hsl(var(--success))]">{financialMetrics?.totalExpectedReturn ?? `${property.targetReturn}%`}</span>} />
                  <StatBox label="Property Value" value={formatCurrency(propertyValue)} />
                  {financialMetrics?.year1CashYield && (
                    <StatBox label="Dividend Yield" value={financialMetrics.year1CashYield} />
                  )}
                </StatGrid>
              ))}
            </div>

            {/* Mobile Investment CTA */}
            <MobileCTA
              onClick={() => !isFunded && onInvest?.()}
              label={isFunded ? 'Fully Funded' : `Invest Now - ${formatCurrency(sharePrice)}/share`}
            />

            {/* Tabs — Page Builder is content authority when published config exists */}
            <div className="mt-6">
              <Tabs key={pageConfig ? 'pb' : 'default'} defaultValue={pageConfig ? 'content' : (isFunded ? 'details' : 'overview')} className="w-full">
                <DetailTabList tabs={
                  pageConfig
                    ? [
                        { value: 'content', label: 'Details' },
                        { value: 'location', label: 'Location' },
                        ...(!isFunded ? [{ value: 'documents', label: 'Documents' }] : []),
                      ]
                    : [
                        ...(isFunded
                          ? [
                              { value: 'details', label: 'Details' },
                              { value: 'strategy', label: 'Strategy' },
                              { value: 'location', label: 'Location' },
                            ]
                          : [
                              { value: 'overview', label: 'Overview' },
                              { value: 'financials', label: 'Financials' },
                              { value: 'location', label: 'Location' },
                              { value: 'documents', label: 'Documents' },
                            ]),
                      ]
                } />

                {/* ── Page Builder Content Tab (primary authority when published config exists) ── */}
                {pageConfig && (
                  <TabsContent value="content" className="mt-6">
                    <PageRenderer
                      config={pageConfig}
                      entity={{ entityType: 'property', entityId: property.id, entityData: property as any }}
                    />
                  </TabsContent>
                )}

                {/* ── Details Tab (funded properties, no page builder) ── */}
                {!pageConfig && isFunded && (
                  <TabsContent value="details" className="mt-6 space-y-6">
                    {tabContent.details ? (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-3">About This Property</h3>
                        {tabContent.details.split('\n').filter(Boolean).map((paragraph, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{paragraph}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground leading-relaxed">No content configured yet.</p>
                    )}

                    {/* Property Details Grid */}
                    <div>
                      <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Property Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                          <div>
                            <p className="text-sm font-semibold text-[#242424] mb-1">Property Type</p>
                            <p className="text-xs text-muted-foreground">{getPropertyTypeDisplay(property.propertyType)}</p>
                          </div>
                        </div>
                        {property.squareFeet && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Total Area</p>
                              <p className="text-xs text-muted-foreground">{property.squareFeet.toLocaleString()} sq ft</p>
                            </div>
                          </div>
                        )}
                        {property.yearBuilt && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Year Built</p>
                              <p className="text-xs text-muted-foreground">{property.yearBuilt}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                          <div>
                            <p className="text-sm font-semibold text-[#242424] mb-1">Total Shares</p>
                            <p className="text-xs text-muted-foreground">{(property.totalShares ?? 0).toLocaleString()} shares</p>
                          </div>
                        </div>
                        {property.holdPeriod && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Hold Period</p>
                              <p className="text-xs text-muted-foreground">{property.holdPeriod}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                )}

                {/* ── Strategy Tab (funded properties, no page builder) ── */}
                {!pageConfig && isFunded && (
                  <TabsContent value="strategy" className="mt-6 space-y-6">
                    {tabContent.strategy ? (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-3">Investment Strategy</h3>
                        {tabContent.strategy.split('\n').filter(Boolean).map((paragraph, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{paragraph}</p>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-3">Investment Strategy</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          No strategy content has been added for this property yet.
                        </p>
                      </div>
                    )}
                  </TabsContent>
                )}

                {/* ── Overview Tab (active properties, no page builder) ── */}
                {!pageConfig && !isFunded && (
                  <TabsContent value="overview" className="mt-6 space-y-6">
                    {tabContent.overview ? (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-3">About This Property</h3>
                        {tabContent.overview.split('\n').filter(Boolean).map((paragraph, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{paragraph}</p>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-3">About This Property</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {property.description || 'No content configured yet.'}
                        </p>
                      </div>
                    )}

                    {/* Property Details Grid */}
                    <div>
                      <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Property Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                          <div>
                            <p className="text-sm font-semibold text-[#242424] mb-1">Property Type</p>
                            <p className="text-xs text-muted-foreground">{getPropertyTypeDisplay(property.propertyType)}</p>
                          </div>
                        </div>
                        {property.squareFeet && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Total Area</p>
                              <p className="text-xs text-muted-foreground">{property.squareFeet.toLocaleString()} sq ft</p>
                            </div>
                          </div>
                        )}
                        {property.yearBuilt && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Year Built</p>
                              <p className="text-xs text-muted-foreground">{property.yearBuilt}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                          <div>
                            <p className="text-sm font-semibold text-[#242424] mb-1">Investors</p>
                            <p className="text-xs text-muted-foreground">{property.investorCount} investors</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                          <div>
                            <p className="text-sm font-semibold text-[#242424] mb-1">Total Shares</p>
                            <p className="text-xs text-muted-foreground">{(property.totalShares ?? 0).toLocaleString()} shares</p>
                          </div>
                        </div>
                        {property.holdPeriod && (
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                            <div>
                              <p className="text-sm font-semibold text-[#242424] mb-1">Hold Period</p>
                              <p className="text-xs text-muted-foreground">{property.holdPeriod}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Investment Highlights */}
                    {investmentHighlights && investmentHighlights.length > 0 && (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Investment Highlights</h3>
                        <div className="space-y-3">
                          {investmentHighlights.map((highlight, index) => (
                            <div key={`highlight-${index}`} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                              <p className="text-sm text-muted-foreground">{highlight}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risk Factors */}
                    <RiskDisclosure
                      title="Investment Risks"
                      description="Real estate investments involve risk including possible loss of principal. Property values can decrease due to market conditions, economic factors, or property-specific issues."
                      expandableContent={
                        <div className="space-y-2 pt-4 border-t border-border text-xs text-muted-foreground">
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Market Risk — Property values can decline due to economic or local market conditions</li>
                            <li>Liquidity Risk — Shares may be difficult to sell; real estate is an illiquid asset class</li>
                            <li>Income Risk — Rental income can fluctuate or cease if tenants vacate</li>
                            <li>Concentration Risk — A single-property investment concentrates risk in one asset</li>
                            <li>Regulatory Risk — Changes in local laws, zoning, or tax policy may affect value</li>
                            <li>Leverage Risk — If the property carries debt, losses can be magnified</li>
                          </ul>
                          <p>
                            <strong>No Guarantee of Returns:</strong> Past performance does not guarantee future results.
                            Target returns are projections only and may not be achieved.
                          </p>
                          <p>
                            <strong>Tax Complexity:</strong> Real estate investments can generate complex tax consequences.
                            Consult your tax advisor about your specific situation.
                          </p>
                        </div>
                      }
                      expandLabel="View Full Risk Disclosure"
                    />
                  </TabsContent>
                )}

                {/* ── Financials Tab (active properties, no page builder) ── */}
                {!pageConfig && !isFunded && (
                  <TabsContent value="financials" className="mt-6 space-y-6">
                    {!financialMetrics && (
                      <p className="text-sm text-muted-foreground text-center py-8">No financial data available</p>
                    )}
                    {financialMetrics && (
                      <div>
                        <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Key Financial Metrics</h3>
                        <StatGrid columns={2}>
                          <StatBox label="Year 1 Cash Yield" value={financialMetrics.year1CashYield} />
                          <StatBox label="Projected 5-Year IRR" value={financialMetrics.projected5YearIRR} />
                          <StatBox label="Appreciation (YTD)" value={financialMetrics.appreciationYTD} />
                          <StatBox label="Total Expected Return" value={financialMetrics.totalExpectedReturn} />
                          <StatBox label="Annual Net Income" value={formatCurrency(financialMetrics.annualNetIncome)} />
                          <StatBox label="Cap Rate" value={financialMetrics.capRate} />
                        </StatGrid>
                      </div>
                    )}
                  </TabsContent>
                )}

                {/* ── Location Tab (all properties) ── */}
                <TabsContent value="location" className="mt-6 space-y-6">
                  <div>
                    <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Location Overview</h3>
                        <PropertyMap
                          address={property.address}
                          city={property.city}
                          state={property.state}
                          propertyName={property.name}
                          className="mb-4"
                        />
                    {tabContent.location ? (
                      tabContent.location.split('\n').filter(Boolean).map((paragraph, i) => (
                        <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{paragraph}</p>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Located in the heart of {property.city}, {property.state}, this property benefits from excellent connectivity,
                        proximity to major employment centers, and a vibrant neighborhood with abundant amenities including
                        shopping, dining, entertainment, and transportation options.
                      </p>
                    )}

                    {/* Market Stats */}
                      {marketStatistics && (
                        <div>
                          <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Market Statistics</h3>
                          <StatGrid columns={2}>
                            <StatBox label="Population Growth (5yr)" value={marketStatistics.populationGrowth} />
                            <StatBox label="Job Growth (5yr)" value={marketStatistics.jobGrowth} />
                            <StatBox label="Median Household Income" value={marketStatistics.medianHouseholdIncome} />
                            <StatBox label="Unemployment Rate" value={marketStatistics.unemploymentRate} />
                          </StatGrid>
                        </div>
                      )}

                      {/* Nearby Amenities */}
                      {nearbyAmenities && nearbyAmenities.length > 0 && (
                        <div>
                          <h3 className="text-lg font-sans font-semibold text-[#242424] mb-4">Nearby Amenities</h3>
                          <div className="space-y-3">
                            {nearbyAmenities.map((amenity, index) => (
                              <div key={`amenity-${index}`} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                <p className="text-sm text-muted-foreground">{amenity.label}: {amenity.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!marketStatistics && (!nearbyAmenities || nearbyAmenities.length === 0) && !tabContent.location && (
                        <p className="text-sm text-muted-foreground text-center py-8">No additional location data available</p>
                      )}
                  </div>
                </TabsContent>

                {/* ── Documents Tab (active properties only) ── */}
                {!isFunded && (
                  <TabsContent value="documents" className="mt-6 space-y-3">
                    {effectiveDocs && effectiveDocs.length > 0 ? (
                      effectiveDocs.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.url || '#'}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => { if (!doc.url && onDocumentDownload) { e.preventDefault(); onDocumentDownload(doc); } }}
                          className="w-full flex items-center justify-between p-4 rounded-lg border border-border hover:bg-[#F0F0F0] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-primary" />
                            <div className="text-left">
                              <p className="text-sm font-semibold text-[#242424]">{doc.name}</p>
                              {doc.size && <p className="text-xs text-muted-foreground">{doc.size}</p>}
                            </div>
                          </div>
                          <Download className="w-4 h-4 text-muted-foreground" />
                        </a>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No documents available</p>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </div>

          {/* Right Column - Investment Sidebar (Desktop) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Investment Card */}
              <Card className="p-6 border-border">
                {isFunded ? (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Funding Progress</span>
                        <span className="font-semibold text-[#242424]">100%</span>
                      </div>
                      <div className="w-full bg-[#F0F0F0] rounded-full h-2">
                        <div className="h-2 rounded-full bg-emerald-500 transition-all duration-300" style={{ width: '100%' }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatCurrency(property.price)} of {formatCurrency(property.price)} raised
                      </p>
                    </div>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-600 cursor-default" size="lg" disabled>
                      Fully Funded
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="mb-6">
                      <p className="text-sm text-muted-foreground mb-2">Share Price</p>
                      <h2 className="text-3xl font-sans font-semibold text-[#242424] mb-3">{formatCurrency(sharePrice)}</h2>
                      {financialMetrics?.appreciationYTD && (
                        <div className="flex items-center gap-2 mb-4 text-[hsl(var(--success))]">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-sm font-semibold">{financialMetrics.appreciationYTD} appreciation</span>
                        </div>
                      )}

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Annual Return</span>
                          <span className="font-semibold text-[#242424]">{financialMetrics?.totalExpectedReturn ?? `${property.targetReturn}%`}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Min. Investment</span>
                          <span className="font-semibold text-[#242424]">{formatCurrency(sharePrice)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Available Shares</span>
                          <span className="font-semibold text-[#242424]">{availableShares.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Funding Progress</span>
                        <span className="font-semibold text-[#242424]">{effectiveFundedPct}%</span>
                      </div>
                      <div className="w-full bg-[#F0F0F0] rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-primary transition-all duration-300"
                          style={{ width: `${effectiveFundedPct}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatCurrency(property.price * property.fundedPercentage / 100)} of {formatCurrency(property.price)} raised
                      </p>
                    </div>

                    <Button
                      className="w-full mb-3"
                      size="lg"
                      onClick={() => onInvest?.()}
                    >
                      Invest Now
                    </Button>
                  </>
                )}
                {/* TODO: Restore post mvp */}
                {/* <Button variant="outline" className="w-full" size="lg" onClick={onDownloadProspectus}>
                  Download Prospectus
                </Button> */}
              </Card>

              {/* Why Invest Card — only show when no page builder config; otherwise use a feature-grid block in the page builder */}
              {!pageConfig && <Card className="p-6 border-border">
                <h3 className="text-base font-sans font-semibold text-[#242424] mb-4">Why Invest?</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F0F0F0] border border-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                      <Percent className="w-4 h-4 text-[#242424]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#242424] mb-1">Passive Income</p>
                      <p className="text-xs text-muted-foreground">Quarterly distributions from rental income</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F0F0F0] border border-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-[#242424]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#242424] mb-1">Appreciation</p>
                      <p className="text-xs text-muted-foreground">Benefit from property value growth</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F0F0F0] border border-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-[#242424]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#242424] mb-1">Diversification</p>
                      <p className="text-xs text-muted-foreground">Add real estate to your portfolio</p>
                    </div>
                  </div>
                </div>
              </Card>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
